import React, {useEffect, useRef, useState} from "react";

// One PDF page drawn by pdf.js, with a text layer over it so the text can be
// selected, copied and searched, and its links clicked. Used by the CV viewer
// (links.tsx) inside the lightbox: the page has the `swiper-zoom-target` class
// so the lightbox's pinch/double-tap zoom scales it.
//
// pdf.js is loaded on demand (only when a PDF is opened), and it's the legacy
// build, which covers the browsers in package.json's browserslist.

type PdfLink = {url: string; left: number; top: number; width: number; height: number};

// The canvas is drawn this much sharper than the page's on-screen size, so it
// stays crisp zoomed in; capped by pixel count (iOS refuses canvases over ~16.7M px).
const ZOOM_SHARPNESS = 2;
const MAX_CANVAS_PX = 16_000_000;

export function PdfPage({src, title}: {src: string; title: string}) {
    const pageRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textRef = useRef<HTMLDivElement>(null);
    // page size in PDF units; A4 until the PDF has loaded
    const [size, setSize] = useState({w: 595.28, h: 841.89});
    const [links, setLinks] = useState<PdfLink[]>([]);
    const [state, setState] = useState<"loading" | "ready" | "error">("loading");

    useEffect(() => {
        let cancelled = false;
        let cleanup = () => {};
        (async () => {
            const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
            // copied into public/ by scripts/pdf-worker.js
            pdfjs.GlobalWorkerOptions.workerSrc = `/pdfjs/pdf.worker-${pdfjs.version}.min.mjs`;
            const doc = await pdfjs.getDocument({url: src, isEvalSupported: false}).promise;
            cleanup = () => { void doc.destroy(); };
            if (cancelled) return cleanup();
            const page = await doc.getPage(1);
            const base = page.getViewport({scale: 1});
            if (cancelled) return;
            setSize({w: base.width, h: base.height});

            // Draw at the largest size the page could be shown at on this
            // screen (it's fitted to the window), times the zoom sharpness.
            const fitW = Math.min(window.screen.width, window.screen.height * base.width / base.height);
            let scale = fitW * (window.devicePixelRatio || 1) * ZOOM_SHARPNESS / base.width;
            scale = Math.min(scale, Math.sqrt(MAX_CANVAS_PX / (base.width * base.height)));
            const viewport = page.getViewport({scale});
            const canvas = canvasRef.current!;
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            await page.render({canvasContext: canvas.getContext("2d")!, viewport}).promise;
            if (cancelled) return;

            // Text layer: laid out in PDF units times --scale-factor, which the
            // ResizeObserver below keeps matched to the page's on-screen width.
            const text = textRef.current!;
            text.replaceChildren();
            await new pdfjs.TextLayer({
                textContentSource: page.streamTextContent(),
                container: text,
                viewport: page.getViewport({scale: 1}),
            }).render();

            // Links, placed as percentages of the page so they need no scaling.
            const annotations = await page.getAnnotations();
            if (cancelled) return;
            setLinks(annotations
                .filter((a: {subtype: string; url?: string}) => a.subtype === "Link" && a.url)
                .map((a: {url: string; rect: number[]}) => {
                    const [x1, y1, x2, y2] = a.rect;
                    return {
                        url: a.url,
                        left: x1 / base.width * 100,
                        top: (base.height - y2) / base.height * 100,
                        width: (x2 - x1) / base.width * 100,
                        height: (y2 - y1) / base.height * 100,
                    };
                }));
            setState("ready");
        })().catch(() => { if (!cancelled) setState("error"); });
        return () => { cancelled = true; cleanup(); };
    }, [src]);

    useEffect(() => {
        const el = pageRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            el.style.setProperty("--scale-factor", String(el.clientWidth / size.w));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [size.w]);

    return (
        <div
            ref={pageRef}
            // swiper-no-swiping: a mouse drag here selects text instead of dragging the slide
            className={`pdf-page swiper-zoom-target swiper-no-swiping ${state}`}
            style={{"--page-ratio": size.w / size.h} as React.CSSProperties}
            onClick={(e) => e.stopPropagation()}
            role="document"
            aria-label={title}
            aria-busy={state === "loading"}
        >
            <canvas ref={canvasRef} aria-hidden="true"/>
            <div ref={textRef} className="textLayer"/>
            {links.map((l, i) => (
                <a
                    key={i}
                    className="pdf-link"
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{left: `${l.left}%`, top: `${l.top}%`, width: `${l.width}%`, height: `${l.height}%`}}
                    aria-label={l.url.replace(/^(https?:|mailto:)\/*/, "")}
                />
            ))}
            {state === "error" && <p className="pdf-error">Couldn't show the CV here. Use Open PDF or Download.</p>}
        </div>
    );
}
